import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHourglassHalf, faCircleCheck, faTriangleExclamation, faGift, faCheck,
  faClock, faPaperclip,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';
import '../../styles/subscription-shared.scss';

const PLAN_OPTIONS = [
  {
    value: 'MONTHLY', badge: 'Populaire', name: 'Plan Mensuel',
    price: '150,00', unit: 'dhs/mois',
    desc: 'Idéal pour démarrer avec un coût initial faible.',
  },
  {
    value: 'YEARLY', badge: null, name: 'Plan Annuel',
    price: '1 620,00', unit: 'dhs/an',
    desc: 'Économisez 10% par rapport au plan mensuel.',
  },
];

const STATUS_META = {
  TRIAL:     { icon: faHourglassHalf,       cls: 'sub-status-icon--trial',   title: "Essai gratuit en cours" },
  ACTIVE:    { icon: faCircleCheck,         cls: 'sub-status-icon--active',  title: 'Abonnement actif' },
  EXPIRED:   { icon: faTriangleExclamation, cls: 'sub-status-icon--expired', title: 'Essai gratuit terminé' },
  CANCELLED: { icon: faTriangleExclamation, cls: 'sub-status-icon--expired', title: 'Abonnement annulé' },
  PENDING:   { icon: faClock,               cls: 'sub-status-icon--trial',   title: 'Virement en attente de vérification' },
};

function SubscribeForm({ status, onSubscribed }) {
  const [plan, setPlan] = useState(status?.subscriptionPlan || '');
  const [bankReference, setBankReference] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!plan) { setError("Veuillez sélectionner un plan d'abonnement."); return; }
    if (!bankReference.trim()) { setError('Veuillez indiquer la référence de votre virement.'); return; }

    setSubmitting(true);
    try {
      const { data: newStatus } = await axios.post(`${API_BASE_URL}/api/account/subscribe`, {
        subscriptionPlan: plan,
        subscriptionMethod: 'BANK',
        stripePaymentMethodId: null,
        bankReference: bankReference.trim(),
      }, { withCredentials: true });

      if (proofFile && newStatus.pendingInvoiceId) {
        const formData = new FormData();
        formData.append('file', proofFile);
        try {
          await axios.post(
            `${API_BASE_URL}/api/account/subscription-invoices/${newStatus.pendingInvoiceId}/proof`,
            formData,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch {
          toast.error("La référence a bien été enregistrée, mais l'envoi du justificatif a échoué. Vous pourrez le renvoyer plus tard.");
        }
      }

      toast.success('Votre demande a été enregistrée — en attente de vérification.');
      onSubscribed(newStatus);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la confirmation de l'abonnement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="rg-cards">
        {PLAN_OPTIONS.map(p => (
          <div
            key={p.value}
            className={`rg-card${plan === p.value ? ' rg-card--selected' : ''}`}
            onClick={() => setPlan(p.value)}
          >
            {p.badge && <div className="rg-card__badge">{p.badge}</div>}
            <div className="rg-card__radio">
              <input type="radio" name="subscriptionPlan" value={p.value} checked={plan === p.value} onChange={() => setPlan(p.value)} />
              <span>{p.name}</span>
            </div>
            <div className="rg-card__price">{p.price} <small>{p.unit}</small></div>
            <div className="rg-card__desc">{p.desc}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 row">
        <div className="col-sm-7">
          <label className="cdm-field-label">Référence du virement <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            placeholder="Référence indiquée sur votre virement"
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
          />
        </div>

        <div className="col-sm-5 mt-3 mt-sm-0">
          <label className="cdm-field-label">Justificatif (optionnel)</label>
          <div>
            <label className="btn btn-outline-secondary btn-sm" style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faPaperclip} className="me-2" />
              {proofFile ? proofFile.name : 'Joindre le reçu de virement'}
              <input
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mt-2 small">{error}</div>}

      <button
        type="button"
        className="sub-confirm-btn"
        onClick={handleSubmit}
        disabled={submitting}
      >
        <FontAwesomeIcon icon={faCheck} />
        {submitting ? 'Confirmation...' : 'Confirmer mon abonnement'}
      </button>
    </div>
  );
}

const SubscriptionPlan = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangeForm, setShowChangeForm] = useState(false);

  const fetchStatus = () => {
    axios.get(`${API_BASE_URL}/api/account/subscription-status`, { withCredentials: true })
      .then(({ data }) => setStatus(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  if (loading) return null;

  const meta = STATUS_META[status?.status] || STATUS_META.TRIAL;

  return (
    <div className="account-details">
      <div className="sub-status-card">
        <div className={`sub-status-icon ${meta.cls}`}>
          <FontAwesomeIcon icon={meta.icon} />
        </div>
        <div>
          <div className="sub-status-title">{meta.title}</div>
          <div className="sub-status-sub">
            {status?.status === 'TRIAL' && `${status.daysRemaining} jour${status.daysRemaining !== 1 ? 's' : ''} restant${status.daysRemaining !== 1 ? 's' : ''} sur votre essai gratuit`}
            {status?.status === 'ACTIVE' && `Plan ${status.subscriptionPlan === 'MONTHLY' ? 'Mensuel' : 'Annuel'} actif`}
            {status?.status === 'EXPIRED' && "Choisissez un plan pour continuer à utiliser Saydalani"}
            {status?.status === 'CANCELLED' && 'Réactivez votre abonnement pour continuer'}
            {status?.status === 'PENDING' && (
              status.pendingReference
                ? `Référence transmise : ${status.pendingReference}`
                : 'Votre virement est en cours de vérification par notre équipe'
            )}
          </div>
          {status?.status === 'ACTIVE' && status.subscriptionStartDate && (
            <div className="sub-status-since">
              Abonné depuis le {new Date(status.subscriptionStartDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          )}
        </div>

        {/* Yearly is a commitment — only a monthly subscriber can switch (up to yearly).
            An active yearly subscriber sees the same link, disabled with an explanatory
            tooltip, rather than have it silently disappear. */}
        {status?.status === 'ACTIVE' && status.subscriptionMethod === 'BANK' && !showChangeForm && (() => {
          const canChange = status.subscriptionPlan === 'MONTHLY';
          return (
            <button
              type="button"
              onClick={canChange ? () => setShowChangeForm(true) : undefined}
              disabled={!canChange}
              data-tooltip-id={!canChange ? 'tooltip' : undefined}
              data-tooltip-content={!canChange ? "Le plan annuel est un engagement : impossible de repasser en mensuel avant la fin de la période en cours." : undefined}
              style={{
                background: 'none', border: 'none', padding: 0, marginLeft: 'auto', alignSelf: 'flex-start',
                color: canChange ? '#185FA5' : '#9ca3af', fontSize: 13, fontWeight: 500,
                cursor: canChange ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap',
              }}
            >
              {canChange ? 'Passer au plan annuel' : 'Changer de plan'}
            </button>
          );
        })()}
      </div>

      {status?.status === 'TRIAL' && status.daysRemaining <= 5 && (
        <div className="sub-trial-banner">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>
            Il ne reste que <strong>{status.daysRemaining} jour{status.daysRemaining !== 1 ? 's' : ''}</strong> d'essai gratuit.
            Choisissez votre plan dès maintenant pour continuer sans interruption.
          </span>
        </div>
      )}

      {status?.status === 'TRIAL' && status.daysRemaining > 5 && (
        <div className="rg-security-note">
          <FontAwesomeIcon icon={faGift} />
          Profitez de votre essai gratuit jusqu'au {status.trialEndDate ? new Date(status.trialEndDate).toLocaleDateString('fr-FR') : '—'}. Vous pouvez choisir un plan à tout moment.
        </div>
      )}

      {status?.status === 'EXPIRED' && status.rejectionReason && (
        <div className="sub-trial-banner">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>
            Votre dernière demande de virement a été refusée : <strong>{status.rejectionReason}</strong>.
            Vérifiez les informations et soumettez une nouvelle référence.
          </span>
        </div>
      )}

      {(['TRIAL', 'EXPIRED', 'CANCELLED'].includes(status?.status) || showChangeForm) && (
        <SubscribeForm status={status} onSubscribed={(s) => { setShowChangeForm(false); setStatus(s); }} />
      )}
    </div>
  );
};

export default SubscriptionPlan;
